// src/components/modules/SpectralBarChart.jsx
import React, { useMemo, useState } from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';
import { Paper, Typography, Box, Stack, ToggleButtonGroup, ToggleButton, useTheme, alpha } from '@mui/material';

const CHANNEL_WAVELENGTHS = {
    f1: 415, f2: 445, f3: 480, f4: 515,
    f5: 555, f6: 590, f7: 630, f8: 680,
    blue: 465, green: 525, red: 615,
};

const SENSOR_CHANNELS = {
    AS7341: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8'],
    APDS9960: ['blue', 'green', 'red'],
};

function detectSensor(data) {
    if (data?.sensorType) return data.sensorType;
    if (data?.f1 != null || data?.f2 != null) return 'AS7341';
    if (data?.red != null || data?.green != null) return 'APDS9960';
    return 'AS7341';
}

/** basic count = RAW / (tint_ms * gain) */
function toBasicCount(raw, tintMs, gain) {
    if (raw == null || !tintMs || !gain) return 0;
    return raw / (tintMs * gain);
}

function fmtVal(v) {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return v.toFixed(1);
}

export default function SpectralBarChart({ data, title, showRef = true }) {
    const theme = useTheme();
    const [mode, setMode] = useState('basic'); // 'basic' | 'raw'

    const sensor = detectSensor(data);
    const channels = SENSOR_CHANNELS[sensor] || SENSOR_CHANNELS.AS7341;
    const isAPDS = sensor === 'APDS9960';

    const tint = data?.timeMs ?? 1;
    const gain = data?.gain ?? 1;
    const refTint = data?.reference?.timeMs ?? tint;
    const refGain = data?.reference?.gain ?? gain;

    const convert = (raw) => mode === 'basic' ? toBasicCount(raw, tint, gain) : (raw ?? 0);
    const convertRef = (raw) => mode === 'basic' ? toBasicCount(raw, refTint, refGain) : (raw ?? 0);

    const chartData = useMemo(() => {
        if (!data) return [];
        return channels.map((ch) => {
            const raw = data[ch] ?? 0;
            const ref = data.reference?.[ch] ?? 0;
            const label = `${ch.toUpperCase()} (${CHANNEL_WAVELENGTHS[ch]}nm)`;
            return {
                channel: label,
                value: convert(raw),
                ref: convertRef(ref),
            };
        });
    }, [data, tint, gain, refTint, refGain, mode, channels, isAPDS]);

    const clearVal = useMemo(() => convert(data?.clear ?? 0), [data, tint, gain, mode]);
    const nirVal = useMemo(() => isAPDS ? null : convert(data?.nir ?? 0), [data, tint, gain, mode, isAPDS]);
    const clearRef = useMemo(() => convertRef(data?.reference?.clear ?? 0), [data, refTint, refGain, mode]);
    const nirRef = useMemo(() => isAPDS ? null : convertRef(data?.reference?.nir ?? 0), [data, refTint, refGain, mode, isAPDS]);

    const yLabel = mode === 'basic' ? 'Basic Count' : 'RAW';

    const { sampleMeta, refMeta } = useMemo(() => {
        const sample = [];
        if (data?.gain != null) sample.push(`G${data.gain}`);
        if (data?.timeMs != null) sample.push(`${data.timeMs.toFixed(0)}ms`);
        const ref = [];
        if (data?.reference) {
            if (data.reference.gain != null) ref.push(`G${data.reference.gain}`);
            if (data.reference.timeMs != null) ref.push(`${data.reference.timeMs.toFixed(0)}ms`);
        }
        return { sampleMeta: sample, refMeta: ref };
    }, [data]);

    const clearNirData = useMemo(() => {
        if (isAPDS || !data) return [];
        const items = [
            { name: 'CLEAR', amostra: clearVal, ref: clearRef },
            { name: 'NIR', amostra: nirVal ?? 0, ref: nirRef ?? 0 },
        ];
        return items;
    }, [clearVal, nirVal, clearRef, nirRef, isAPDS, data]);

    if (!data || chartData.length === 0) return null;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                    {title}
                </Typography>
                {data?.sensorType && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {data.sensorType}
                    </Typography>
                )}
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, v) => v && setMode(v)}
                    size="small"
                    sx={{ ml: 'auto' }}
                >
                    <ToggleButton value="basic" sx={{ px: 1, py: 0.25, fontSize: 10, textTransform: 'none' }}>
                        Basic
                    </ToggleButton>
                    <ToggleButton value="raw" sx={{ px: 1, py: 0.25, fontSize: 10, textTransform: 'none' }}>
                        RAW
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {sampleMeta.length > 0 && (
                <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5} flexWrap="wrap" useFlexGap>
                    <Typography component="span" sx={{ fontSize: 9, color: 'text.disabled', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        amostra
                    </Typography>
                    {sampleMeta.map((t) => (
                        <Box key={t} component="span" sx={{
                            px: 0.75, py: 0.15, borderRadius: 0.8,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontSize: 10, fontWeight: 600, lineHeight: 1.4,
                        }}>{t}</Box>
                    ))}
                    {refMeta.length > 0 && (
                        <>
                            <Box sx={{ mx: 0.25, width: '1px', height: 14, bgcolor: 'divider' }} />
                            <Typography component="span" sx={{ fontSize: 9, color: 'text.disabled', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                ref
                            </Typography>
                            {refMeta.map((t) => (
                                <Box key={t} component="span" sx={{
                                    px: 0.75, py: 0.15, borderRadius: 0.8,
                                    bgcolor: alpha(theme.palette.text.disabled, 0.1),
                                    color: theme.palette.text.secondary,
                                    fontSize: 10, fontWeight: 600, lineHeight: 1.4,
                                }}>{t}</Box>
                            ))}
                        </>
                    )}
                </Stack>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <ResponsiveContainer width={!isAPDS ? '75%' : '100%'} height={200}>
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: 8, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                        dataKey="channel"
                        tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                        axisLine={false}
                        tickLine={false}
                        width={58}
                        tickFormatter={fmtVal}
                        label={{ value: yLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' }, fontSize: 9, fill: theme.palette.text.secondary }}
                    />
                    <Tooltip
                        contentStyle={{
                            fontSize: 12,
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                        formatter={(val, name) => [val.toFixed(2), name === 'Amostra' ? 'Amostra' : 'Referência']}
                    />
                    <Legend
                        verticalAlign="top"
                        height={24}
                        iconType="plainline"
                        wrapperStyle={{ fontSize: 11 }}
                    />
                    {showRef && data.reference && (
                        <Line
                            dataKey="ref"
                            name="Referência"
                            stroke={theme.palette.text.disabled}
                            strokeWidth={1.5}
                            strokeDasharray="5 3"
                            dot={{ r: 3, fill: theme.palette.text.disabled }}
                            type="monotone"
                        />
                    )}
                    <Line
                        dataKey="value"
                        name="Amostra"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ r: 3, fill: theme.palette.primary.main }}
                        activeDot={{ r: 5 }}
                        type="monotone"
                    />
                </LineChart>
                </ResponsiveContainer>

                {!isAPDS && clearNirData.length > 0 && (
                    <ResponsiveContainer width="25%" height={200}>
                        <BarChart data={clearNirData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
                            <YAxis
                                dataKey="name"
                                type="category"
                                tick={{ fontSize: 9, fill: theme.palette.text.secondary, fontWeight: 600 }}
                                axisLine={false}
                                tickLine={false}
                                width={40}
                            />
                            <XAxis
                                type="number"
                                tick={{ fontSize: 8, fill: theme.palette.text.secondary }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={fmtVal}
                            />
                            <Tooltip
                                contentStyle={{
                                    fontSize: 11,
                                    background: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                                formatter={(val, name) => [val.toFixed(2), name === 'amostra' ? 'Amostra' : 'Referência']}
                            />
                            {showRef && data.reference && (
                                <Bar dataKey="ref" name="Referência" fill={theme.palette.text.disabled} barSize={6} radius={[0, 3, 3, 0]} />
                            )}
                            <Bar dataKey="amostra" name="Amostra" fill={theme.palette.primary.main} barSize={6} radius={[0, 3, 3, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Paper>
    );
}
